import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid } from "recharts";
import { IndianRupee, ShoppingBag, TrendingUp, Users, AlertTriangle } from "lucide-react";
import api, { money } from "../../lib/api";
import { AdminHeader } from "./AdminLayout";

const Stat = ({ label, value, icon: Icon, accent }) => (
  <div className="bg-white border border-sage/30 p-5 rounded-sm">
    <div className="flex items-center justify-between mb-2"><span className="text-xs uppercase tracking-wide text-forest/50">{label}</span><Icon size={16} className={accent || "text-moss"} /></div>
    <div className="serif text-3xl">{value}</div>
  </div>
);

export default function AdminDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then(r => setD(r.data)); }, []);
  if (!d) return <div className="space-y-4"><div className="h-10 w-64 shimmer" /><div className="grid grid-cols-4 gap-4">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-28 shimmer" />)}</div></div>;

  return (
    <div>
      <AdminHeader title="Dashboard" subtitle="Overview of your store performance" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat label="Today's Sales" value={money(d.today_sales)} icon={IndianRupee} />
        <Stat label="Total Revenue" value={money(d.revenue)} icon={TrendingUp} />
        <Stat label="Paid Orders" value={d.paid_orders} icon={ShoppingBag} />
        <Stat label="Avg Order Value" value={money(d.aov)} icon={IndianRupee} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-8">
        {[["Total", d.total_orders], ["Pending", d.pending], ["Processing", d.processing], ["Shipped", d.shipped], ["Delivered", d.delivered], ["Customers", d.customer_count]].map(([l, v]) => (
          <div key={l} className="bg-beige/40 p-4 rounded-sm"><div className="text-xs uppercase tracking-wide text-forest/50">{l}</div><div className="serif text-2xl">{v}</div></div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white border border-sage/30 p-6 rounded-sm">
          <h3 className="serif text-2xl mb-4">Sales (last 14 days)</h3>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={d.sales_series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e6e0d4" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Line type="monotone" dataKey="sales" stroke="#263D2B" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-sage/30 p-6 rounded-sm">
          <h3 className="serif text-2xl mb-4">Top Products</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={d.top_products}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e6e0d4" />
              <XAxis dataKey="name" tick={{ fontSize: 9 }} interval={0} angle={-15} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Bar dataKey="units" fill="#5B705C" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {d.low_stock.length > 0 && (
        <div className="bg-white border border-brown/30 p-6 rounded-sm">
          <h3 className="serif text-2xl mb-4 flex items-center gap-2"><AlertTriangle size={18} className="text-brown" /> Low Stock</h3>
          <div className="flex flex-wrap gap-3">
            {d.low_stock.map((p) => <Link key={p.slug} to="/admin/inventory" className="text-sm bg-beige/50 px-3 py-1.5">{p.name} · {p.inventory?.available ?? 0} left</Link>)}
          </div>
        </div>
      )}
    </div>
  );
}
